import { Document, Packer, Paragraph, TextRun, HeadingLevel, Header, Footer } from 'docx';
import PizZip from 'pizzip';

interface TextSegment {
  text: string;
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  font?: string;
  link?: string;
}

/**
 * Parses inline markdown formatting in a text string
 * Supports: **bold**, *italic*, `code`, [link](url)
 */
function parseInlineMarkdown(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let currentIndex = 0;
  let currentText = '';

  while (currentIndex < text.length) {
    const remaining = text.slice(currentIndex);

    // Check for code blocks first (backticks) - highest priority
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      // Save any accumulated text
      if (currentText) {
        segments.push({ text: currentText });
        currentText = '';
      }
      // Add code segment
      segments.push({
        text: codeMatch[1],
        font: 'Courier New',
      });
      currentIndex += codeMatch[0].length;
      continue;
    }

    // Check for bold (**text** or __text__) - must be doublear
    const boldMatch = remaining.match(/^(\*\*|__)(.+?)\1/);
    if (boldMatch && boldMatch[2].length > 0) {
      // Save any accumulated text
      if (currentText) {
        segments.push({ text: currentText });
        currentText = '';
      }
      // Recursively parse the bold content (may contain other formatting)
      const boldContent = parseInlineMarkdown(boldMatch[2]);
      // Apply bold to all segments in the bold content
      boldContent.forEach((seg) => {
        segments.push({ ...seg, bold: true });
      });
      currentIndex += boldMatch[0].length;
      continue;
    }

    // Check for italic (*text* or _text_) - single, but not if followed by same char
    // Avoid matching ** as italic
    const italicMatch = remaining.match(/^([*_])([^*_\n]+?)\1(?!\1)/);
    if (italicMatch && italicMatch[2].length > 0) {
      // Save any accumulated text
      if (currentText) {
        segments.push({ text: currentText });
        currentText = '';
      }
      // Recursively parse the italic content
      const italicContent = parseInlineMarkdown(italicMatch[2]);
      // Apply italic to all segments in the italic content
      italicContent.forEach((seg) => {
        segments.push({ ...seg, italics: true });
      });
      currentIndex += italicMatch[0].length;
      continue;
    }

    // Check for links [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      // Save any accumulated text
      if (currentText) {
        segments.push({ text: currentText });
        currentText = '';
      }
      // Parse link text for any nested formatting
      const linkTextSegments = parseInlineMarkdown(linkMatch[1]);
      linkTextSegments.forEach((seg) => {
        segments.push({
          ...seg,
          link: linkMatch[2],
          underline: true,
        });
      });
      currentIndex += linkMatch[0].length;
      continue;
    }

    // Regular character - accumulate
    currentText += text[currentIndex];
    currentIndex++;
  }

  // Add any remaining text
  if (currentText) {
    segments.push({ text: currentText });
  }

  return segments;
}

/**
 * Converts text segments to TextRun objects for docx
 */
function segmentsToTextRuns(segments: TextSegment[]): TextRun[] {
  return segments.map((seg) => {
    if (seg.link) {
      return new TextRun({
        text: seg.text,
        bold: seg.bold,
        italics: seg.italics,
        underline: seg.underline ? {} : undefined,
        font: seg.font,
      });
    }
    return new TextRun({
      text: seg.text,
      bold: seg.bold,
      italics: seg.italics,
      underline: seg.underline ? {} : undefined,
      font: seg.font,
    });
  });
}

function getParagraphsFromMarkdown(markdown: string): Paragraph[] {
  const lines = markdown.split('\n');
  const paragraphs: Paragraph[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      // Empty line - add spacing
      paragraphs.push(
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { after: 120 },
        })
      );
      continue;
    }

    // Simple heading detection
    if (trimmedLine.startsWith('#')) {
      const level = Math.min(trimmedLine.match(/^#+/)?.[0].length || 1, 6);
      const text = trimmedLine.replace(/^#+\s*/, '');
      const segments = parseInlineMarkdown(text);
      const textRuns = segments.map((seg) => {
        return new TextRun({
          text: seg.text,
          bold: seg.bold || true, // Headings are always bold
          italics: seg.italics,
          underline: seg.underline ? {} : undefined,
          font: seg.font,
          size: 24 - (level - 1) * 2,
        });
      });

      paragraphs.push(
        new Paragraph({
          children: textRuns,
          heading: [
            HeadingLevel.HEADING_1,
            HeadingLevel.HEADING_2,
            HeadingLevel.HEADING_3,
            HeadingLevel.HEADING_4,
            HeadingLevel.HEADING_5,
            HeadingLevel.HEADING_6,
          ][level - 1],
          spacing: { before: 200, after: 200 },
        })
      );
      continue;
    }

    // Basic bullet list detection (unordered lists only)
    if (trimmedLine.match(/^(\s*)[-*+]\s/)) {
      const leadingSpaces = line.length - trimmedLine.length;
      const depth = Math.floor(leadingSpaces / 2);
      const text = trimmedLine.replace(/^(\s*)[-*+]\s*/, '');
      const segments = parseInlineMarkdown(text);
      const textRuns = segmentsToTextRuns(segments);

      paragraphs.push(
        new Paragraph({
          children: textRuns,
          bullet: { level: depth },
          indent: { left: (depth + 1) * 720 }, // 720 TWIPs = 0.5 inches per level
          spacing: { before: 120, after: 120 },
        })
      );
      continue;
    }

    // Everything else is a regular paragraph with markdown parsing
    const segments = parseInlineMarkdown(trimmedLine);
    const textRuns = segmentsToTextRuns(segments);

    paragraphs.push(
      new Paragraph({
        children: textRuns,
        spacing: { before: 120, after: 120 },
      })
    );
  }

  return paragraphs;
}

export async function convertMarkdownToDocx(markdown: string): Promise<Blob> {
  const paragraphs = getParagraphsFromMarkdown(markdown);

  const doc = new Document({
    sections: [{ children: paragraphs }],
  });

  return Packer.toBlob(doc);
}

function extractBodyContent(xml: string): string {
  // Get everything inside <w:body>...</w:body>, minus the trailing <w:sectPr>
  const bodyMatch = xml.match(/<w:body>([\s\S]*)<\/w:body>/);
  if (!bodyMatch) throw new Error('No body found in throwaway doc');

  let bodyContent = bodyMatch[1];

  // Remove the sectPr (section properties) - it's always the last element in body
  bodyContent = bodyContent.replace(/<w:sectPr[\s\S]*?<\/w:sectPr>\s*$/, '');

  return bodyContent.trim();
}

// The `word/document.xml` in the template has a <w:body> that contains only a <w:sectPr> (section properties) element.
// We want to replace the body content with our generated paragraphs, but keep the original sectPr to preserve page settings, margins, etc.
function spliceBodyContent(templateXml: string, newContent: string): string {
  // Extract template's sectPr to preserve it
  const sectPrMatch = templateXml.match(/(<w:sectPr[\s\S]*?<\/w:sectPr>)\s*<\/w:body>/);
  if (!sectPrMatch) throw new Error('No sectPr found in template');

  const templateSectPr = sectPrMatch[1];

  // Replace entire body content, keeping template's sectPr
  return templateXml.replace(
    /<w:body>[\s\S]*<\/w:body>/,
    `<w:body>${newContent}\n${templateSectPr}\n</w:body>`
  );
}

// We create a throwaway doc to leverage docx's formatting capabilities, then splice the generated XML into our template
// Remember: .docx is only a zip file with XML inside, so we can manipulate it directly
async function injectParagraphsIntoTemplate(
  templateArrayBuffer: ArrayBuffer,
  docxJsParagraphs: Paragraph[]
): Promise<Blob> {
  const throwawayDoc = new Document({ sections: [{ children: docxJsParagraphs }] });
  const throwawayBlob = await Packer.toBlob(throwawayDoc);
  const throwawayBuffer = await throwawayBlob.arrayBuffer();
  const throwawayZip = new PizZip(throwawayBuffer);

  const throwawayXml = throwawayZip.file('word/document.xml')!.asText();
  const throwawayBodyContent = extractBodyContent(throwawayXml);

  const templateZip = new PizZip(templateArrayBuffer);
  const templateXml = templateZip.file('word/document.xml')!.asText();
  const newXml = spliceBodyContent(templateXml, throwawayBodyContent);
  templateZip.file('word/document.xml', newXml);

  // Copy numbering.xml from throwaway into template
  const throwawayNumbering = throwawayZip.file('word/numbering.xml');
  if (throwawayNumbering) {
    templateZip.file('word/numbering.xml', throwawayNumbering.asText());
    ensureNumberingRelationship(templateZip);
  }

  return templateZip.generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

function ensureNumberingRelationship(zip: PizZip) {
  const relsPath = 'word/_rels/document.xml.rels';
  let relsXml = zip.file(relsPath)!.asText();

  // Only add if not already present
  if (!relsXml.includes('numbering.xml')) {
    relsXml = relsXml.replace(
      '</Relationships>',
      `<Relationship Id="rIdNumbering" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/numbering" Target="numbering.xml"/>\n</Relationships>`
    );
    zip.file(relsPath, relsXml);
  }
}

async function generateDocFromMarkdown(templateUrl: string, markdown: string): Promise<Blob> {
  // Load template file
  const response = await fetch(templateUrl);

  const templateArrayBuffer = await response.arrayBuffer();

  const docxJsParagraphs = getParagraphsFromMarkdown(markdown);

  return injectParagraphsIntoTemplate(templateArrayBuffer, docxJsParagraphs);
}

export async function convertMarkdownToTemplateDocx(
  markdown: string,
  templateName: 'GE' | 'AJ'
): Promise<Blob> {
  return generateDocFromMarkdown(`template/${templateName}.docx`, markdown);
}

export function downloadDocx(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TEST_MARKDOWN = `
# Heading Level 1

This is a normal paragraph with **bold**, *italic*, \`inline code\`, and a [basic link](https://example.com).

## Heading Level 2 with Formatting

This heading contains **bold text**, *italic text*, and \`code\`.

### Nested Formatting Tests

You should support **bold with *italic inside*** correctly.

You should also support *italic with **bold inside*** correctly.

Mixed nesting example:
**bold \`code inside bold\` still bold**

Another nesting:
*italic \`code inside italic\` still italic*

Combined nesting:
**bold and *italic and \`code\` together*** end.
`;
